import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Calculator } from 'lucide-react';
import { ForgotPasswordModal } from '../components/ForgotPasswordModal';
import { BRAND } from '../brand';

const LoginApp = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Completa todos los campos'); return; }
    setLoading(true);
    setError('');
    setErrorCode('');
    try {
      await login(email.trim().toLowerCase(), password);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error_code) {
        setErrorCode(detail.error_code);
        setError(detail.message);
      } else if (typeof detail === 'string') {
        setError(detail);
      } else if (!err.response) {
        setError('Servidor no disponible. Espera unos segundos e intenta de nuevo.');
      } else {
        setError('Error al iniciar sesion. Intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col justify-center px-6 py-12">
      <div className="max-w-sm w-full mx-auto">
        {/* Logo */}
        <div className="text-center mb-10">
          <img 
            src="/logo-oficial.png" 
            alt={`${BRAND.name}`} 
            className="h-44 mx-auto mb-4"
          />
          <h1 className="font-manrope text-2xl font-bold text-slate-900 tracking-tight" data-testid="login-title">
            Bienvenido
          </h1>
          <p className="text-slate-500 text-sm mt-1">Ingresa a tu cuenta {BRAND.name}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div
              className={`text-sm p-4 rounded-xl border ${
                errorCode === 'USER_NOT_FOUND'
                  ? 'bg-blue-50 text-blue-800 border-blue-200'
                  : errorCode === 'WRONG_PASSWORD'
                  ? 'bg-amber-50 text-amber-800 border-amber-200'
                  : 'bg-red-50 text-red-600 border-red-100'
              }`}
              data-testid="login-error"
            >
              <div className="flex items-start gap-2">
                <span className="text-lg flex-shrink-0">
                  {errorCode === 'USER_NOT_FOUND' && '👋'}
                  {errorCode === 'WRONG_PASSWORD' && '🔑'}
                  {!errorCode && '⚠️'}
                </span>
                <div className="flex-1">
                  <p className="font-semibold leading-snug">{error}</p>
                  {errorCode === 'USER_NOT_FOUND' && (
                    <Link
                      to="/register"
                      className="inline-flex items-center mt-3 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-3 py-2 rounded-lg transition shadow"
                      data-testid="error-register-link"
                    >
                      Crear mi cuenta gratis →
                    </Link>
                  )}
                  {errorCode === 'WRONG_PASSWORD' && (
                    <button
                      type="button"
                      onClick={() => setShowForgot(true)}
                      className="inline-flex items-center mt-3 bg-green-600 hover:bg-green-700 text-white font-bold text-xs px-3 py-2 rounded-lg transition shadow"
                      data-testid="error-recover-link"
                    >
                      Recuperar con código al correo
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Correo electrónico</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-12 bg-slate-50 border border-slate-200 rounded-xl px-4 text-base focus:ring-2 focus:ring-green-500/20 focus:border-green-600 transition-all outline-none placeholder:text-slate-400"
              placeholder="tu@email.com"
              autoComplete="email"
              data-testid="login-email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Contraseña</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-12 bg-slate-50 border border-slate-200 rounded-xl px-4 pr-12 text-base focus:ring-2 focus:ring-green-500/20 focus:border-green-600 transition-all outline-none placeholder:text-slate-400"
                placeholder="Tu contraseña"
                autoComplete="current-password"
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
              >
                {showPw ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-green-700 hover:bg-green-800 text-white rounded-xl font-semibold text-base shadow-sm transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="login-submit"
          >
            {loading ? 'Ingresando...' : 'Ingresar'}
          </button>
        </form>

        <div className="mt-3 text-center">
          <button
            type="button"
            onClick={() => setShowForgot(true)}
            className="text-sm text-green-700 font-semibold hover:text-green-800"
            data-testid="forgot-password-link"
          >
            ¿Olvidaste tu contraseña?
          </button>
        </div>

        {/* Cotizar sin ingresar - Secondary CTA */}
        <div className="mt-5">
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-3 bg-white text-slate-400 font-medium">¿solo quieres ver la tasa?</span>
            </div>
          </div>
          <Link
            to="/cotizar"
            className="w-full h-12 flex items-center justify-center gap-2 bg-white border-2 border-green-700 text-green-700 hover:bg-green-50 active:scale-[0.98] rounded-xl font-semibold text-base transition-all"
            data-testid="login-cotizar-sin-ingresar"
          >
            <Calculator size={18} />
            Cotizar sin ingresar
          </Link>
          <p className="text-center text-xs text-slate-400 mt-2">
            Calcula cuánto recibirá en {BRAND?.landing?.dest?.name || 'destino'} en segundos
          </p>
        </div>

        {/* Links */}
        <div className="mt-6 text-center space-y-3">
          <p className="text-slate-500 text-sm">
            ¿No tienes cuenta?{' '}
            <Link to="/register" className="text-green-700 font-semibold hover:text-green-800" data-testid="register-link">
              Crear cuenta
            </Link>
          </p>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <a 
            href="/terminos" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-slate-400 hover:text-slate-500"
          >
            Términos y Condiciones
          </a>
        </div>
      </div>

      {showForgot && (
        <ForgotPasswordModal initialEmail={email} onClose={() => setShowForgot(false)} />
      )}
    </div>
  );
};

export default LoginApp;
